import {
  NEW_PLAN_ID,
  PLAN_TYPE_DESTINATION,
  PLAN_TYPE_SOURCE,
} from '../CreateRecommend/RecommendSourceDestination/constants';
import FREQUENCY from '../../../constants/Frequency';
import {
  OVRecommendActionEnum,
  GenerateOVRPotBatchArguments,
} from '../../../schema-types/globalTypes';
import {RECOMMENDATION_SUBTYPE_TO_SERVER} from '../../../constants/SolutionType';
import {getCurrencyFloat} from '../../../utils/currencyFormatter';
import validateUUID from '../../../utils/validateUUID';
import {
  nonEmptyStringOrNull,
  createOrUpdateSubplan,
  updateRecsVarsProps,
  getPotContactId,
  updateRecommendPotsVariables,
  getIsMinor,
} from './helpers';
import {getPlanFeesServerFromFormik} from '../CreateRecommend/PlanFees';
import OWNERSHIP from '../../../constants/Ownership';

import {UpsertOvrecommendVariables} from '../../../schema-types/UpsertOvrecommend';
import {getFeeServerFromFormik} from '../CreateRecommend/DAInitialFees';
import saltus from '../../../shared/logger';

const getTransferVariables = async ({
  accountId,
  goalId,
  ovrecommendId,
  values,
  destinationPots,
  recommendDetails,
  contacts = [],
}: updateRecsVarsProps): Promise<UpsertOvrecommendVariables> => {
  const {contact, urgency, urgencyReason} = values;
  const isMinor = getIsMinor(contact, contacts);
  const transfer = values.transfer!;
  const isNewDestinationPlan = transfer?.destinationAccount === 'newPlan';
  let productProviderId: string | undefined;
  let productNameId: string | undefined;

  productProviderId = transfer.newPlan?.planProvider;
  productNameId = transfer.newPlan?.planType;

  if (!isNewDestinationPlan) {
    // CREATE OVRECCPOT FOR SUBPLAN WITH EXISTING POT ID
    // UPDATE OVRECCPOT FOR SUBPLAN WITH AMOUNTS
    // DO NOT TOUCH THE MAIN PLAN WHATSOEVER
    const mainPlanPot = destinationPots?.find(
      (x) => x.id === transfer.destinationAccount
    );
    productProviderId = mainPlanPot?.productProvider?.id;
    productNameId = mainPlanPot?.productName?.id;
  }

  return {
    action: OVRecommendActionEnum.TRANSFER,
    ovrecommendId: validateUUID(ovrecommendId)!,
    accountId: validateUUID(accountId)!,
    goalId: validateUUID(goalId)!,
    productProviderId,
    productNameId,
    isNewPlanRequired: isNewDestinationPlan,
    contactId: getPotContactId(contact, isMinor),
    urgency,
    urgencyReason,
    solutionType: RECOMMENDATION_SUBTYPE_TO_SERVER.PRODUCT_BASED,
    parentGuardianId: isNewDestinationPlan
      ? validateUUID(transfer.newPlan?.parentGuardianId)
      : undefined,
  };
};

export const generateTransferOVRPots = async ({
  accountId,
  goalId,
  ovrecommendId,
  values,
  generateRecommendationPots,
  destinationPots,
  reviewId,
  contacts = [],
}: updateRecommendPotsVariables) => {
  const {contact, urgency, urgencyReason} = values;
  const transfer = values.transfer!;
  const isMinor = getIsMinor(contact, contacts);

  const checkedSources = [...(Object.values(transfer?.transferSources || {}) || [])];
  const potArgs: GenerateOVRPotBatchArguments[] = [];
  const isNewDestinationPlan = transfer?.destinationAccount === 'newPlan';
  let productProviderId: string | undefined;
  let productNameId: string | undefined;

  for await (const source of checkedSources) {
    const potId = nonEmptyStringOrNull(source?.id);

    const sourceIsShellPlan = potId?.includes('shell');

    const singleClientAmount = getCurrencyFloat(
      source.full
        ? source.planValue || source.maxAmount || source?.singleClientAmount
        : source?.singleClientAmount || 0
    );
    const sourcePotId = (sourceIsShellPlan ? source.originalShellPlanId : potId) || null;
    const sourceContactId = sourceIsShellPlan
      ? source.contactId === OWNERSHIP.JOINT_CODE && !isMinor
        ? null
        : source.contactId || null
      : null;
    const updateSourcePotVariables: GenerateOVRPotBatchArguments = {
      potId: sourcePotId ? validateUUID(sourcePotId) : null,
      inSpecie: source?.inSpecie,
      reviewId: sourceIsShellPlan ? validateUUID(reviewId) : undefined,

      originalOvrId: sourceIsShellPlan ? validateUUID(ovrecommendId) : undefined,
      planType: PLAN_TYPE_SOURCE,
      planNumber: source?.accountNumber,
      productProviderId: source?.planProvider,
      productNameId: source?.planType,
      isDummy: sourceIsShellPlan,
      initialFee: values.isSppDa ? undefined : source.initialFee,
      legacyInitialFee: getFeeServerFromFormik(values.isSppDa, source?.legacyInitialFee),
      isAuthorityHeld: sourceIsShellPlan
        ? source.isDataGather === 'yes' && source.isAuthorityHeld === 'yes'
        : undefined,
      isDataGather: sourceIsShellPlan ? source.isDataGather === 'yes' : undefined,
      full: source?.full || false,
      currentDirectValue: sourceIsShellPlan
        ? getCurrencyFloat(source?.planValue)
        : undefined, // only set plan value if it's a shell plan
      contactId: validateUUID(sourceContactId), // help
      singleClientAmount,
    };
    saltus.log({updateSourcePotVariables});

    if (sourceIsShellPlan) {
      updateSourcePotVariables.planStatus = 'In force';
    }

    potArgs.push(updateSourcePotVariables);
  }

  const newDestinationPotId = transfer.newPlan?.existingPotId || null;
  // Note, when destinationAccount is NEW_PLAN_ID, destinationIsNewPlan is always true
  if (transfer.destinationAccount === NEW_PLAN_ID) {
    const newPlanFields = transfer.newPlan!;
    const destinationPotVariables: GenerateOVRPotBatchArguments = {
      potId: newDestinationPotId ? validateUUID(newDestinationPotId) : null,
      isDummy: true,

      planType: PLAN_TYPE_DESTINATION,
      singleClientAmount: getCurrencyFloat(values.transfer?.combinedOneOffMovingAmount),
      reviewId: validateUUID(reviewId),
      contactId: getPotContactId(contact, isMinor),
      fees: getPlanFeesServerFromFormik(newPlanFields.planFees),
      hasFeeDeviation: newPlanFields.planFees.feeDeviation,
      mandate: typeof newPlanFields.model === 'string' ? newPlanFields.model : null,
      modelPortfolioId:
        typeof newPlanFields.model === 'string'
          ? null
          : validateUUID(newPlanFields.model?.id || ''),
      productProviderId: newPlanFields.planProvider,
      productNameId: newPlanFields.planType,
      inTrust: newPlanFields.inTrust,
      originalOvrId: validateUUID(ovrecommendId),
    };
    productProviderId = newPlanFields.planProvider;
    productNameId = newPlanFields.planType;
    potArgs.push(destinationPotVariables);

    if (transfer.newPlan!.subplan0?.checked) {
      potArgs.push(
        createOrUpdateSubplan({
          isDummy: true,
          childPotId: transfer.newPlan!.subplan0?.existingPotId,
          subplanObj: transfer.newPlan!.subplan0!,
          reviewId,
          frequency: FREQUENCY.SINGLE,
          accountId,
          ovrecommendId,
          contactId: getPotContactId(contact, isMinor),
        })
      );
    }

    if (transfer.newPlan!.subplan1?.checked) {
      potArgs.push(
        createOrUpdateSubplan({
          isDummy: true,
          childPotId: transfer.newPlan!.subplan1?.existingPotId,
          subplanObj: transfer.newPlan!.subplan1!,
          reviewId,
          frequency: FREQUENCY.SINGLE,
          accountId,
          ovrecommendId,
          contactId: getPotContactId(contact, isMinor),
        })
      );
    }
  } else if (transfer.destinationAccount) {
    // CREATE OVRECCPOT FOR SUBPLAN WITH EXISTING POT ID
    // UPDATE OVRECCPOT FOR SUBPLAN WITH AMOUNTS
    // DO NOT TOUCH THE MAIN PLAN WHATSOEVER
    const mainPlanPot = destinationPots?.find(
      (x) => x.id === transfer.destinationAccount
    );
    productProviderId = mainPlanPot?.productProvider?.id;
    productNameId = mainPlanPot?.productName?.id;

    const subplans = Object.entries(transfer.destinationValues.subplans || {}).filter(
      ([id, fields]) => fields.checked
    );

    if (subplans?.length) {
      const potId = nonEmptyStringOrNull(transfer.destinationAccount);
      if (potId) {
        potArgs.push({
          potId: validateUUID(potId),
          planType: PLAN_TYPE_DESTINATION,
          singleClientAmount: getCurrencyFloat(
            values.transfer?.combinedOneOffMovingAmount
          ),
        });
      }
      // IF THERE ARE SUBPLANS:
      // Go through all subplans of the main pot and add their values
      for await (const [subPlanId, subplanForm] of subplans) {
        potArgs.push({
          potId: validateUUID(subPlanId),
          planType: PLAN_TYPE_DESTINATION,
          singleClientAmount: getCurrencyFloat(subplanForm.singleClientAmount || 0),
        });
      }
    } else {
      // if there are no subplans:
      // CREATE OVRECCPOT FOR MAIN PLAN WITH AMOUNTS

      potArgs.push({
        potId: validateUUID(transfer.destinationAccount),
        planType: PLAN_TYPE_DESTINATION,
        singleClientAmount: getCurrencyFloat(
          values.transfer?.combinedOneOffMovingAmount || 0
        ),
      });
    }
  }

  await generateRecommendationPots({
    variables: {
      accountId: validateUUID(accountId)!,
      ovrecommendId: validateUUID(ovrecommendId)!,
      args: potArgs,
    },
  });

  return {
    action: OVRecommendActionEnum.TRANSFER,
    ovrecommendId,
    accountId,
    goalId,
    productProviderId,
    productNameId,
    isNewPlanRequired: isNewDestinationPlan,
    contactId: getPotContactId(contact, isMinor),
    urgency,
    urgencyReason,
    solutionType: RECOMMENDATION_SUBTYPE_TO_SERVER.PRODUCT_BASED,
    // withdrawalReason: nonEmptyStringOrNull(formikTransferReasonToServer(values)),
    // transferReason: nonEmptyStringOrNull(formikTransferReasonToServer(values)),
    contributionFrequency: FREQUENCY.SINGLE, // this is needed by back end but not by front end
  };
};

export default getTransferVariables;
